import React from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Package } from 'lucide-react';

interface ScaleMetric {
  name: string;
  value: number;
  color: string;
}

interface SuccessCriterion {
  id: string;
  description: string;
}

interface DefinitionItem {
  id: string;
  description: string;
}

interface SolutionProductProps {
  title: string;
  description: string;
  purpose: string;
  scaleMetrics: ScaleMetric[];
  successCriteria: SuccessCriterion[];
  definitionOfDone: DefinitionItem[];
}

const SectionContainer = styled(Box)(({ theme }) => ({
  background: 'rgba(26, 26, 46, 0.6)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  padding: theme.spacing(3),
  marginBottom: theme.spacing(4),
  boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
}));

const MetricsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(3),
}));

const MetricItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(1.5),
  borderRadius: theme.spacing(1),
  minWidth: '80px',
}));

const MetricValue = styled(Typography)<{ color: string }>(({ theme, color }) => ({
  fontSize: '1.5rem',
  fontWeight: 'bold',
  color: color,
}));

const MetricName = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  color: 'rgba(255, 255, 255, 0.7)',
}));

const ListItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(1),
  '&:last-child': {
    marginBottom: 0,
  },
}));

const Bullet = styled(Box)(({ theme }) => ({
  minWidth: '20px',
  color: '#00CC99',
  fontWeight: 'bold',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: '#fff',
  marginBottom: theme.spacing(2),
  fontWeight: 600,
}));

const SolutionProduct: React.FC<SolutionProductProps> = ({
  title,
  description,
  purpose,
  scaleMetrics,
  successCriteria,
  definitionOfDone,
}) => {
  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Package size={20} color="#8A2BE2" style={{ marginRight: 8 }} />
        <Typography variant="h5" sx={{ color: '#fff' }}>
          Solution Product
        </Typography>
      </Box>

      <SectionContainer>
        <Typography variant="h6" sx={{ color: '#fff', mb: 1 }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 3 }}>
          {description}
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.6)', mb: 1 }}>
            Purpose:
          </Typography>
          <Typography variant="body1" sx={{ color: '#fff' }}>
            {purpose}
          </Typography>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.6)', mb: 1 }}>
            Scale (1-10):
          </Typography>
          <MetricsContainer>
            {scaleMetrics.map((metric) => (
              <MetricItem key={metric.name} sx={{ background: `${metric.color}20` }}>
                <MetricName>{metric.name}</MetricName>
                <MetricValue color={metric.color}>{metric.value}</MetricValue>
              </MetricItem>
            ))}
          </MetricsContainer>
        </Box>

        <Box sx={{ mb: 3 }}>
          <SectionTitle variant="subtitle1">Success Criteria:</SectionTitle>
          {successCriteria.map((criterion) => (
            <ListItem key={criterion.id}>
              <Bullet>•</Bullet>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                {criterion.description}
              </Typography>
            </ListItem>
          ))}
        </Box>

        <Box>
          <SectionTitle variant="subtitle1">Definition of Done:</SectionTitle>
          {definitionOfDone.map((item) => (
            <ListItem key={item.id}>
              <Bullet>•</Bullet>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                {item.description}
              </Typography>
            </ListItem>
          ))}
        </Box>
      </SectionContainer>
    </Box>
  );
};

export default SolutionProduct; 